import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Mail, Plus, Trash2, PlugZap, RefreshCw, ChevronDown, ChevronRight, Loader2, Eye, EyeOff, CheckCircle2, AlertTriangle,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Slider } from "./ui/slider";
import { api } from "../lib/api";
import { SenderChipInput, SenderMatchTester } from "./SenderChipInput";

// Settings → Integrations → Email Intake card.
// Manages any number of independent IMAP mailboxes (NZRA, Manheim,
// direct customers, etc). Each mailbox has its own credentials,
// allowlist, prompt house-rules and after-hours behaviour — nothing is
// shared. Poller ticks every 15s; disabled mailboxes are ignored.

const DEFAULT_FORM = {
  label: "",
  enabled: false,
  imap_host: "",
  imap_port: 993,
  imap_username: "",
  imap_password: "",
  use_ssl: true,
  folder: "INBOX",
  poll_interval_seconds: 60,
  // Feb 2026 chip-refit — allowlist and blocklist are now array<string>
  // in the form state (was comma-separated free text). The chip
  // component validates + sanitises each entry as it's added, so
  // there's no round-trip through parseList and no way to smuggle an
  // invisible unicode char into a stored pattern.
  sender_allowlist: [],
  sender_blocklist: [],
  // Feb 2026 — per-mailbox ignore phrases. Case-insensitive substring
  // match on subject + body + PDF text; silent skip before Claude
  // extraction so obvious non-jobs (release notices, payment
  // confirmations, OOO replies) don't burn LLM credits.
  ignore_phrases: [],
  auto_route_trigger_phrases: [],
  subject_filter: "",
  auto_route_after_hours: false,
  auto_route_min_confidence: 0.8,
  house_rules_prompt: "",
};

function fmtRelative(iso) {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  const secs = Math.round((Date.now() - then) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

export default function EmailIntakeCard() {
  const [mailboxes, setMailboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});   // id -> bool
  const [drafts, setDrafts] = useState({});       // id -> form draft
  const [showPass, setShowPass] = useState({});
  const [testing, setTesting] = useState({});
  const [saving, setSaving] = useState({});
  const [newDraft, setNewDraft] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/intake/mailboxes");
      setMailboxes(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load mailboxes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const startEdit = (m) => {
    setExpanded((prev) => ({ ...prev, [m.id]: true }));
    setDrafts((prev) => ({
      ...prev,
      [m.id]: {
        ...DEFAULT_FORM,
        ...m,
        imap_password: "",  // Never populated from server — leave blank.
        // Feb 2026 chip-refit — pass arrays straight through, no join.
        sender_allowlist: Array.isArray(m.sender_allowlist) ? m.sender_allowlist : [],
        sender_blocklist: Array.isArray(m.sender_blocklist) ? m.sender_blocklist : [],
        ignore_phrases: Array.isArray(m.ignore_phrases) ? m.ignore_phrases : [],
        auto_route_trigger_phrases: Array.isArray(m.auto_route_trigger_phrases) ? m.auto_route_trigger_phrases : [],
      },
    }));
  };

  const buildPayload = (draft) => ({
    label: draft.label,
    enabled: !!draft.enabled,
    imap_host: draft.imap_host,
    imap_port: parseInt(draft.imap_port, 10) || 993,
    imap_username: draft.imap_username,
    imap_password: draft.imap_password || null,
    use_ssl: !!draft.use_ssl,
    folder: draft.folder || "INBOX",
    poll_interval_seconds: parseInt(draft.poll_interval_seconds, 10) || 60,
    sender_allowlist: Array.isArray(draft.sender_allowlist) ? draft.sender_allowlist : [],
    sender_blocklist: Array.isArray(draft.sender_blocklist) ? draft.sender_blocklist : [],
    ignore_phrases: Array.isArray(draft.ignore_phrases) ? draft.ignore_phrases : [],
    auto_route_trigger_phrases: Array.isArray(draft.auto_route_trigger_phrases) ? draft.auto_route_trigger_phrases : [],
    subject_filter: draft.subject_filter || null,
    auto_route_after_hours: !!draft.auto_route_after_hours,
    auto_route_min_confidence: Number(draft.auto_route_min_confidence) || 0.8,
    house_rules_prompt: draft.house_rules_prompt || null,
  });

  const saveNew = async () => {
    if (!newDraft?.label?.trim()) { toast.error("Label required"); return; }
    if (!newDraft?.imap_host?.trim()) { toast.error("IMAP host required"); return; }
    if (!newDraft?.imap_username?.trim()) { toast.error("Username required"); return; }
    if (!newDraft?.imap_password) { toast.error("Password required on create"); return; }
    setSaving((p) => ({ ...p, "__new__": true }));
    try {
      await api.post("/intake/mailboxes", buildPayload(newDraft));
      toast.success(`Mailbox "${newDraft.label}" added — leave it disabled until you've tested the connection.`);
      setNewDraft(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving((p) => ({ ...p, "__new__": false }));
    }
  };

  const saveEdit = async (id) => {
    const d = drafts[id];
    if (!d) return;
    setSaving((p) => ({ ...p, [id]: true }));
    try {
      await api.put(`/intake/mailboxes/${id}`, buildPayload(d));
      toast.success(`Saved "${d.label}"`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving((p) => ({ ...p, [id]: false }));
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete mailbox "${m.label}"? This does not delete any previous intake rows or jobs — just stops polling.`)) return;
    try {
      await api.delete(`/intake/mailboxes/${m.id}`);
      toast.message(`Removed "${m.label}"`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const testConnection = async (id) => {
    setTesting((p) => ({ ...p, [id]: true }));
    try {
      const { data } = await api.post(`/intake/mailboxes/${id}/test`);
      if (data?.ok) {
        toast.success(`Connected · ${data.message_count} messages in ${data.folder_exists ? "folder" : "unknown folder"}`);
      } else {
        toast.error(data?.error || "Test failed");
      }
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTesting((p) => ({ ...p, [id]: false }));
    }
  };

  const pollNow = async (id) => {
    setTesting((p) => ({ ...p, [`poll-${id}`]: true }));
    try {
      const { data } = await api.post(`/intake/mailboxes/${id}/poll-now`);
      toast.success(`Poll complete — ${data.intake_rows_created} new intake row(s)`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Poll failed");
    } finally {
      setTesting((p) => ({ ...p, [`poll-${id}`]: false }));
    }
  };

  const renderForm = (draft, setDraft, keyPrefix) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div className="md:col-span-2">
        <Label className="text-xs">Label</Label>
        <Input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} placeholder="NZRA, Manheim POs, etc." data-testid={`${keyPrefix}-label`} />
      </div>
      <div>
        <Label className="text-xs">IMAP host</Label>
        <Input value={draft.imap_host} onChange={(e) => setDraft({ ...draft, imap_host: e.target.value })} placeholder="imap.gmail.com" data-testid={`${keyPrefix}-host`} />
      </div>
      <div>
        <Label className="text-xs">Port</Label>
        <Input type="number" value={draft.imap_port} onChange={(e) => setDraft({ ...draft, imap_port: e.target.value })} data-testid={`${keyPrefix}-port`} />
      </div>
      <div>
        <Label className="text-xs">Username</Label>
        <Input value={draft.imap_username} onChange={(e) => setDraft({ ...draft, imap_username: e.target.value })} placeholder="you@example.com" data-testid={`${keyPrefix}-user`} />
      </div>
      <div>
        <Label className="text-xs">Password / app password {keyPrefix !== "new" && <span className="text-slate-400">(blank = keep current)</span>}</Label>
        <div className="relative">
          <Input
            type={showPass[keyPrefix] ? "text" : "password"}
            value={draft.imap_password}
            onChange={(e) => setDraft({ ...draft, imap_password: e.target.value })}
            placeholder={keyPrefix === "new" ? "app-password" : "•••••• (leave blank to keep)"}
            data-testid={`${keyPrefix}-pass`}
            className="pr-9"
          />
          <button
            type="button"
            onClick={() => setShowPass((p) => ({ ...p, [keyPrefix]: !p[keyPrefix] }))}
            className="absolute right-1 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
          >
            {showPass[keyPrefix] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <div>
        <Label className="text-xs">Folder</Label>
        <Input value={draft.folder} onChange={(e) => setDraft({ ...draft, folder: e.target.value })} placeholder="INBOX" data-testid={`${keyPrefix}-folder`} />
      </div>
      <div>
        <Label className="text-xs">Poll every (seconds)</Label>
        <Input type="number" min={15} value={draft.poll_interval_seconds} onChange={(e) => setDraft({ ...draft, poll_interval_seconds: e.target.value })} data-testid={`${keyPrefix}-interval`} />
      </div>
      <div className="flex items-center gap-3 md:col-span-2">
        <div className="flex items-center gap-2">
          <Switch checked={!!draft.use_ssl} onCheckedChange={(v) => setDraft({ ...draft, use_ssl: v })} data-testid={`${keyPrefix}-ssl`} />
          <Label className="text-xs">Use SSL (recommended)</Label>
        </div>
      </div>

      <div className="md:col-span-2">
        <Label className="text-xs">
          Sender allowlist <span className="text-rose-600 font-semibold">(required — leave blank to disable ingestion)</span>
          {" · use "}<code>@domain</code>{" to match a whole domain, or an exact "}<code>user@domain</code>
        </Label>
        <SenderChipInput
          value={draft.sender_allowlist}
          onChange={(next) => setDraft({ ...draft, sender_allowlist: next })}
          placeholder="operations@nzra.co.nz or @nzra.co.nz — press Enter or comma to add"
          testid={`${keyPrefix}-allow`}
        />
        <SenderMatchTester
          allowlist={draft.sender_allowlist}
          blocklist={draft.sender_blocklist}
          testid={`${keyPrefix}-allow-tester`}
        />
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs">Sender blocklist (blocklist wins over allowlist)</Label>
        <SenderChipInput
          value={draft.sender_blocklist}
          onChange={(next) => setDraft({ ...draft, sender_blocklist: next })}
          placeholder="noreply@somewhere.com or @somewhere.com"
          testid={`${keyPrefix}-block`}
        />
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs">
          Ignore phrases <span className="text-slate-500">(any match on subject / body / PDF → skip before Claude)</span>
        </Label>
        <SenderChipInput
          value={draft.ignore_phrases}
          onChange={(next) => setDraft({ ...draft, ignore_phrases: next })}
          placeholder="release notice, out of office, payment received"
          testid={`${keyPrefix}-ignore`}
          validate={(s) => s.length >= 3 && s.length <= 200}
          validateHint="needs 3-200 chars (any text)"
        />
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs">
          Auto-route trigger phrases <span className="text-slate-500">(any match → skip confidence check, auto-create the job)</span>
        </Label>
        <SenderChipInput
          value={draft.auto_route_trigger_phrases}
          onChange={(next) => setDraft({ ...draft, auto_route_trigger_phrases: next })}
          placeholder="please collect and store, tow to salvage yard"
          testid={`${keyPrefix}-trigger`}
          validate={(s) => s.length >= 3 && s.length <= 200}
          validateHint="needs 3-200 chars (any text)"
        />
        <div className="text-[11px] text-slate-500 mt-1">
          Use for phrases your partner always includes on real jobs (e.g. Turners always writes "please collect and store"). Bypasses the min-confidence threshold below. Address, phone and vehicle must still be present (we can't route a driver without them) — they just don't have to score above the threshold. Impound safety rail still applies.
        </div>
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs">Subject filter (substring, case-insensitive · leave blank to match all)</Label>
        <Input value={draft.subject_filter || ""} onChange={(e) => setDraft({ ...draft, subject_filter: e.target.value })} placeholder="Job details" data-testid={`${keyPrefix}-subject`} />
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs">House-rules prompt (appended to the extractor prompt — sender-specific quirks)</Label>
        <Textarea value={draft.house_rules_prompt || ""} onChange={(e) => setDraft({ ...draft, house_rules_prompt: e.target.value })} className="text-xs font-mono h-24" placeholder="e.g. 'This sender puts the claim number on the last line before the disclaimer.'" data-testid={`${keyPrefix}-rules`} />
      </div>

      <div className="md:col-span-2 border-t border-slate-200 dark:border-slate-700 pt-3 mt-1">
        <div className="flex items-center gap-3">
          <Switch checked={!!draft.auto_route_after_hours} onCheckedChange={(v) => setDraft({ ...draft, auto_route_after_hours: v })} data-testid={`${keyPrefix}-autoroute`} />
          <Label className="text-xs font-semibold">Auto-route to on-call driver after hours</Label>
        </div>
        {draft.auto_route_after_hours && (
          <div className="mt-2 ml-10">
            <Label className="text-xs">Min confidence to auto-route: <span className="font-semibold">{Math.round((draft.auto_route_min_confidence ?? 0.8) * 100)}%</span></Label>
            <Slider
              value={[Number(draft.auto_route_min_confidence ?? 0.8) * 100]}
              onValueChange={(v) => setDraft({ ...draft, auto_route_min_confidence: (v[0] || 80) / 100 })}
              min={70} max={95} step={5}
              className="mt-1 max-w-xs"
              data-testid={`${keyPrefix}-confidence`}
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Only auto-routes if address, phone, vehicle all above this confidence AND sender is in allowlist AND any booked_for is within 12h.
            </div>
          </div>
        )}
      </div>

      <div className="md:col-span-2 border-t border-slate-200 dark:border-slate-700 pt-3">
        <div className="flex items-center gap-3">
          <Switch checked={!!draft.enabled} onCheckedChange={(v) => setDraft({ ...draft, enabled: v })} data-testid={`${keyPrefix}-enabled`} />
          <Label className="text-xs font-semibold">
            {draft.enabled ? "🟢 Enabled — poller will fetch new emails automatically" : "⚪ Disabled — poller will skip this mailbox"}
          </Label>
        </div>
      </div>
    </div>
  );

  return (
    <section
      className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-6"
      data-testid="settings-email-intake-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display text-lg font-black text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-600" />
            Email Intake
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Watch one or more IMAP inboxes and turn incoming rescue emails / POs into pre-filled job drafts.
            Each mailbox is fully independent — its own creds, allowlist, prompt tuning and after-hours rules.
          </p>
        </div>
        {!newDraft && (
          <Button
            size="sm"
            onClick={() => setNewDraft({ ...DEFAULT_FORM })}
            className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
            data-testid="add-mailbox-btn"
          >
            <Plus className="h-4 w-4" /> Add mailbox
          </Button>
        )}
      </div>

      {loading && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
      {!loading && mailboxes.length === 0 && !newDraft && (
        <div className="text-sm text-slate-500 dark:text-slate-400 border border-dashed border-slate-300 dark:border-slate-700 rounded-md p-6 text-center">
          No mailboxes yet. Add one and keep it disabled until you&apos;ve tested the connection.
        </div>
      )}

      <div className="space-y-3">
        {mailboxes.map((m) => {
          const isOpen = !!expanded[m.id];
          const draft = drafts[m.id];
          return (
            <div key={m.id} className="border border-slate-200 dark:border-slate-700 rounded-md" data-testid={`mailbox-${m.id}`}>
              <button
                onClick={() => {
                  if (!isOpen && !draft) startEdit(m);
                  setExpanded((p) => ({ ...p, [m.id]: !isOpen }));
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50"
              >
                {isOpen ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{m.label || "(unnamed)"}</span>
                    {m.enabled ? (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">Enabled</span>
                    ) : (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300">Disabled</span>
                    )}
                    {m.auto_route_after_hours && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">Auto-route</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {m.imap_username} · {m.imap_host}:{m.imap_port}
                    {" · last poll "}{fmtRelative(m.last_poll_at)}
                    {m.last_intake_count > 0 && ` · ${m.last_intake_count} new last poll`}
                  </div>
                  {m.last_error && (
                    <div className="text-xs text-rose-600 dark:text-rose-400 mt-0.5 flex items-start gap-1 whitespace-pre-line break-words">
                      <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                      <span>{m.last_error}</span>
                    </div>
                  )}
                </div>
              </button>

              {isOpen && draft && (
                <div className="border-t border-slate-200 dark:border-slate-700 p-4 space-y-4 bg-slate-50/50 dark:bg-slate-900/50">
                  {renderForm(draft, (v) => setDrafts((p) => ({ ...p, [m.id]: v })), `edit-${m.id}`)}
                  <div className="flex flex-wrap items-center gap-2 pt-2">
                    <Button size="sm" onClick={() => testConnection(m.id)} disabled={!!testing[m.id]} variant="outline" data-testid={`test-${m.id}`}>
                      {testing[m.id] ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <PlugZap className="h-3.5 w-3.5 mr-1" />}
                      Test connection
                    </Button>
                    <Button size="sm" onClick={() => pollNow(m.id)} disabled={!!testing[`poll-${m.id}`]} variant="outline" data-testid={`poll-${m.id}`}>
                      {testing[`poll-${m.id}`] ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                      Poll now
                    </Button>
                    <div className="flex-1" />
                    <Button size="sm" onClick={() => remove(m)} variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`delete-${m.id}`}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                    </Button>
                    <Button size="sm" onClick={() => saveEdit(m.id)} disabled={!!saving[m.id]} className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white" data-testid={`save-${m.id}`}>
                      {saving[m.id] ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                      Save
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {newDraft && (
          <div className="border-2 border-blue-500 rounded-md p-4 bg-blue-50/40 dark:bg-blue-950/20" data-testid="new-mailbox-form">
            <div className="font-semibold text-sm text-blue-900 dark:text-blue-100 mb-3">New mailbox</div>
            {renderForm(newDraft, setNewDraft, "new")}
            <div className="flex justify-end gap-2 pt-3">
              <Button size="sm" variant="outline" onClick={() => setNewDraft(null)} data-testid="cancel-new-mailbox">Cancel</Button>
              <Button size="sm" onClick={saveNew} disabled={!!saving["__new__"]} className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white" data-testid="save-new-mailbox">
                {saving["__new__"] ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                Add mailbox
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
